/**
 * Centralized API Manager for Flight Data
 * Handles rate limiting, request queuing, and caching to prevent API limit exceeded errors
 */

import fallbackManager from './fallbackManager';
import { getRemoteString } from '../remoteConfig';

// Build Aviation Edge proxy URL using Remote Config parameter
function buildAviationEdgeProxyUrl(endpoint, params = {}) {
  const proxyBase =
    getRemoteString(
      'aviationedge_proxy_url',
      'https://us-central1-live-flight-tracker---web.cloudfunctions.net/aviationEdgeProxy'
    ) || '';
  const url = new URL(proxyBase);
  url.searchParams.set('endpoint', endpoint);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      url.searchParams.set(k, String(v));
    }
  });
  return url.toString();
}

class APIManager {
  constructor() {
    this.requestQueue = [];
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.minRequestInterval = 300; // Reduced to 300ms for faster requests
    this.maxConcurrentRequests = 5; // Increased to 5 for better throughput
    this.activeRequests = 0;
    this.retryAttempts = 2; // Reduced retry attempts for faster failure recovery
    this.retryDelay = 1000; // Reduced to 1 second
  }

  /**
   * Add request to queue with priority
   */
  async queueRequest(url, options = {}, priority = 'normal') {
    return new Promise((resolve, reject) => {
      // Remove priority from options to avoid passing it to fetch
      const { priority: _, ...cleanOptions } = options;
      
      const request = {
        url,
        options: cleanOptions,
        priority,
        resolve,
        reject,
        timestamp: Date.now(),
        attempts: 0
      };

      // Insert based on priority
      if (priority === 'high') {
        this.requestQueue.unshift(request);
      } else {
        this.requestQueue.push(request);
      }

      this.processQueue();
    });
  }

  /**
   * Process the request queue with rate limiting
   */
  async processQueue() {
    if (this.requestQueue.length === 0 || this.activeRequests >= this.maxConcurrentRequests) {
      return;
    }

    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    // Check if we need to wait for the next slot
    if (timeSinceLastRequest < this.minRequestInterval) {
      if (this.isProcessing) return;
      this.isProcessing = true;
      
      const waitTime = this.minRequestInterval - timeSinceLastRequest;
      await this.delay(waitTime);
      
      this.isProcessing = false;
      this.processQueue();
      return;
    }

    // Dispatch as many requests as we can within concurrency limits
    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const request = this.requestQueue.shift();
      this.activeRequests++;
      this.lastRequestTime = Date.now(); // Update last request time for EACH dispatch
      
      // We don't await executeRequest here so they run concurrently
      this.executeRequest(request);
      
      // If we still have more slots, we must still respect the interval for the NEXT one
      // So we break and let the next processQueue call handle it after the interval
      if (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
        setTimeout(() => this.processQueue(), this.minRequestInterval);
        break;
      }
    }
  }

  /**
   * Execute a single request with retry logic
   */
  async executeRequest(request) {
    try {
      this.lastRequestTime = Date.now();
      
      const response = await fetch(request.url, {
        ...request.options,
        signal: AbortSignal.timeout(15000) // Reduced to 15 second timeout for faster failure recovery
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      request.resolve(data);
    } catch (error) {
      console.warn(`API request failed (attempt ${request.attempts + 1}):`, error.message);
      
      request.attempts++;
      
      // Check if we should use fallback data
      if (fallbackManager.shouldUseFallback(error)) {
        console.log('🔄 Using fallback data due to API error');
        request.resolve([]); // Return empty array, fallback will be handled by caller
        this.activeRequests--;
        this.processQueue();
        return;
      }
      
      if (request.attempts < this.retryAttempts) {
        // Retry with exponential backoff
        const delay = this.retryDelay * Math.pow(2, request.attempts - 1);
        setTimeout(() => {
          this.requestQueue.unshift(request);
          this.processQueue();
        }, delay);
      } else {
        request.reject(error);
      }
    } finally {
      this.activeRequests--;
      this.processQueue();
    }
  }

  /**
   * Get flight data directly from Aviation Edge API (like Android app)
   */
  async getAviationEdgeFlights(limit = 7000) {
    try {
      console.log(`🚀 Fetching ${limit} flights directly from Aviation Edge API`);
      const url = buildAviationEdgeProxyUrl('flights', { limit });
      const response = await fetch(url, { 
        signal: AbortSignal.timeout(20000),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        console.log(`✅ Successfully fetched ${data.length} flights from Aviation Edge`);
        return data;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch Aviation Edge flights:', error);
      
      // Use fallback data if API fails
      if (fallbackManager.shouldUseFallback(error)) {
        console.log('🔄 Aviation Edge API failed, using fallback flights');
        return fallbackManager.getFallbackFlights(limit);
      }
      
      return [];
    }
  }

  /**
   * Get flight details from Aviation Edge timetable API (like Android app)
   */
  async getFlightDetailsFromAviationEdge(iataNumber, iataCode, flightNumber) {
    try {
      console.log(`🔍 Fetching flight details for ${iataNumber} from Aviation Edge`);
      
      // First try arrival API
      let detailsData = null;
      try {
        const arrivalUrl = buildAviationEdgeProxyUrl('timetable', {
          iataCode,
          type: 'arrival',
          flight_num: flightNumber,
        });
        const arrivalResponse = await fetch(arrivalUrl, { 
          signal: AbortSignal.timeout(20000),
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (arrivalResponse.ok) {
          const arrivalData = await arrivalResponse.json();
          if (Array.isArray(arrivalData) && arrivalData.length > 0) {
            detailsData = arrivalData[0];
            console.log(`✅ Found flight details via arrival API for ${iataNumber}`);
          }
        }
      } catch (error) {
        console.warn(`Arrival API failed for ${iataNumber}:`, error.message);
      }

      // If arrival API failed, try departure API
      if (!detailsData) {
        try {
          const departureUrl = buildAviationEdgeProxyUrl('timetable', {
            iataCode,
            type: 'departure',
            flight_num: flightNumber,
          });
          const departureResponse = await fetch(departureUrl, { 
            signal: AbortSignal.timeout(20000),
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });

          if (departureResponse.ok) {
            const departureData = await departureResponse.json();
            if (Array.isArray(departureData) && departureData.length > 0) {
              detailsData = departureData[0];
              console.log(`✅ Found flight details via departure API for ${iataNumber}`);
            }
          }
        } catch (error) {
          console.warn(`Departure API failed for ${iataNumber}:`, error.message);
        }
      }

      return detailsData || null;
    } catch (error) {
      console.error('Failed to fetch flight details from Aviation Edge:', error);
      return null;
    }
  }

  async getAirlineTimetable(airlineIata, onUpdate = null) {
    try {
      console.log(`🔍 Fetching airline timetable for ${airlineIata} (Incremental Mode)`);
      
      let allFoundFlights = [];
      const seenFids = new Set();

      const addBatch = (batch) => {
        if (!Array.isArray(batch)) return;
        
        let newAdded = false;
        batch.forEach(flight => {
          const fid = flight.flight?.iataNumber || flight.flight?.number;
          if (fid && !seenFids.has(fid)) {
            seenFids.add(fid);
            allFoundFlights.push(flight);
            newAdded = true;
          }
        });

        if (newAdded && typeof onUpdate === 'function') {
          onUpdate([...allFoundFlights]);
        }
      };

      // Fire off requests in parallel but process them as they complete
      const reqs = [
          { name: 'live', url: buildAviationEdgeProxyUrl('flights', { airlineIata: airlineIata }) },
          { name: 'operating', url: buildAviationEdgeProxyUrl('timetable', { airline_iata: airlineIata }) },
          { name: 'marketing', url: buildAviationEdgeProxyUrl('timetable', { codeshared_airline_iata: airlineIata }) }
      ];

      // Use Promise.all with individual wrappers to call addBatch immediately
      await Promise.all(reqs.map(async (req) => {
          try {
              const data = await this.queueRequest(req.url, { priority: req.name === 'live' ? 'high' : 'normal' });
              addBatch(data);
          } catch (err) {
              console.warn(`Batch ${req.name} failed for ${airlineIata}:`, err.message);
          }
      }));
      
      return allFoundFlights;
    } catch (error) {
      console.error('Failed to fetch airline timetable:', error);
      return [];
    }
  }


  /**
   * Utility function for delays
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueLength: this.requestQueue.length,
      activeRequests: this.activeRequests,
      isProcessing: this.isProcessing
    };
  }
}

// Create singleton instance
const apiManager = new APIManager();

export default apiManager;
